<template>
  <transition name="modal">
    <div
      v-if="active"
      class="preferred-po-modal-component-container"
    >
      <div class="content">
        <div
          class="close"
          @click="hide()"
        >
          <icon
            name="cross"
          />
        </div>
        <div class="left">
          <upsell-carousel
            ref="carousel"
            :class="{ showOptions: showOptions }"
            :slides="slides"
          />
        </div>
        <div class="right">
          <template v-if="showOptions">
            <img
              v-if="mobileOptionImage"
              class="mobile-options-image"
              :src="mobileOptionImage"
            >
            <div class="heading options-heading">
              Choose a plan
            </div>
            <ul class="options-container">
              <li
                v-for="po in purchaseOrders"
                :key="po.id"
                :class="{ selected: po.id == selected.id }"
                @click="select(po)"
              >
                <div class="bullet" />
                <div class="text">
                  <div class="first">
                    {{ firstLineText(po) }}
                  </div>
                  <div
                    v-if="!showDefaultTrialCopy"
                    class="second"
                  >
                    {{ secondLineText(po) }}
                  </div>
                </div>
              </li>
            </ul>
            <purchase-form
              ref="optionSubmitForm"
              :action="legacyLink"
              :partner-ref="partnerRef"
              :purchase-order="selected"
              :sc="sc"
              :user="user"
            >
              <mastery-button @mastery-button-clicked="triggerOptionSubmit()">
                {{ ctaText }}
              </mastery-button>
            </purchase-form>
          </template>
          <template v-else>
            <div class="heading">
              {{ headingText }}
            </div>
            <div
              class="subheading"
              v-html="subheadingText"
            ></div>
            <purchase-form
              ref="preferredSubmitForm"
              :action="legacyLink"
              :partner-ref="partnerRef"
              :purchase-order="preferredPurchaseOrder"
              :sc="sc"
              :user="user"
            >
              <mastery-button @mastery-button-clicked="triggerPreferredSubmit()">
                {{ ctaText }}
              </mastery-button>
            </purchase-form>
            <button
              class="options"
              @click="toggleOptions"
            >
              View Membership Options
            </button>
          </template>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import MasteryButton from '@components/ui/masteryButton';
import PurchaseForm from '@components/singletons/purchaseForm';
import Icon from '@components/singletons/icon';
import UpsellCarousel from '@components/purchasing/upsellCarousel';

// logic for default trials
import TrialMixin from '@/mixins/trial';

import { legacyFormLink } from '@/utils';

export default {
  components: { MasteryButton, PurchaseForm, Icon, UpsellCarousel },
  mixins: [TrialMixin],
  data() {
    return {
      active: false,
      partnerRef: undefined,
      sc: undefined,
      selected: null,
      showOptions: false,
    };
  },
  computed: {
    ctaText() {
      const upgradeTrialProdVar = this.$store.getters['productVars/get']['preferredUpgradeTrialButtonText'];
      if (this.showDefaultTrialCopy) {
        return upgradeTrialProdVar ? upgradeTrialProdVar.value : 'Join now';
      } else if (this.showPaidTrialCopy) {
        return upgradeTrialProdVar ? upgradeTrialProdVar.value : 'Start your trial';
      } else {
        const upgradeProdVar = this.$store.getters['productVars/get']['preferredUpgradeButtonText'];
        return upgradeProdVar ? upgradeProdVar.value : 'Subscribe now';
      }
    },
    headingText() {
      const headerText = this.$store.getters['app/preferredModalHeader'];
      if (headerText) {
        return headerText;
      } else if (this.showDefaultTrialCopy) {
        return 'Become a member today.';
      } else if (this.showPaidTrialCopy) {
        return 'Try free for ' + this.trialDurationText + '.';
      } else {
        return 'Start your membership.';
      }
    },
    legacyLink() {
      return legacyFormLink(this.$store);
    },
    listener() {
      return this.$store.getters['ui/upsellModalStatus'];
    },
    mobileOptionImage() {
      const slide = this.slides[0];

      if (slide) {
        return this.$store.getters['attachments/get'][slide.attachment]?.url;
      }
    },
    preferredPurchaseOrder() {
      const classification = this.$store.getters['session/classification'];
      return this.purchaseOrders.find(po => po.id === classification.preferredPurchaseOrderId);
    },
    purchaseOrders() {
      return this.$store.getters['purchaseOrders/subscription'].filter(po => po.classificationId === this.$store.getters['session/classification'].id && po.showOnWeb);
    },
    slides() {
      const pv = this.$store.getters['productVars/get']['preferredPoModalSlides'];
      if (pv) {
        const data = JSON.parse(pv.value);
        return data[this.$store.getters['session/classification'].id].sort((a, b) => a.order - b.order);
      } else {
        return [];
      }
    },
    showDefaultTrialCopy() {
      // if app is set up for free trial this takes precedent
      return this.freeTrialEnabled;
    },
    showPaidTrialCopy() {
      // if paid trial is enabled and user has never had paid trial receipt
      return this.trialEnabled && !this.$store.getters['purchaseReceipts/all'].find(pr => pr.isSubscriptionTrial);
    },
    subheadingText() {
      const pv = this.$store.getters['productVars/get']['preferredFooterTextWeb'];
      const subHeaderText = this.$store.getters['app/preferredModalSubHeader'];
      if (subHeaderText) {
        return subHeaderText;
      } else if (pv) {
        return pv.value;
      } else if (this.showDefaultTrialCopy) {
        return 'Access all questions, references, & more!<br>Renews automatically. Cancel anytime.';
      } else if (this.preferredPurchaseOrder?.oneTimeSub) {
        return `$${this.price(this.preferredPurchaseOrder)} billed for ${this.timeframe(this.preferredPurchaseOrder, true)}`;
      } else {
        return `${this.trialPrefixText } $${this.price(this.preferredPurchaseOrder)} / ${this.timeframe(this.preferredPurchaseOrder, true)}.`;
      }
    },
    trialPrefixText() {
      let prefixCopy = '';
      if (this.trialDays && this.showPaidTrialCopy) {
        prefixCopy = this.trialDurationText + ' free. Then ';
      }
      return prefixCopy;
    },
    user() {
      return this.$store.getters['session/currentUser'];
    },
  },
  created() {
    this.$watch('listener', (newValue, oldValue) => {
      if (newValue && !oldValue) {
        this.toggle();
      }
    });
    this.$watch('$route', (newRoute, oldRoute) => {
      if (this.active) {
        if (newRoute !== oldRoute) {
          this.hide();
        }
      }
    });
    if (this.slides) {
      const attachmentIds = this.slides.map(s => s.attachment);
      this.$store.dispatch('attachments/fetch', { ids: attachmentIds });
    }
  },
  methods: {
    hide() {
      // we need to disable the timer in the carousel
      const timer = this.$refs.carousel.timer;
      if (timer) clearInterval(timer);
      this.$analytics.events.dismissedPreferredSubscriptionScreen();
      this.active = false;
    },
    firstLineText(po) {
      if (po.preferredModalHeader) {
        return po.preferredModalHeader;
      } else if (po.oneTimeSub) {
        return `$${this.price(po)} billed once`;
      } else {
        return `$${this.price(po)} billed ${this.timeframe(po)}`;
      }
    },
    secondLineText(po) {
      if (po.preferredModalSubHeader) {
        return po.preferredModalSubHeader;
      } else if (po.oneTimeSub) {
        return `${this.trialPrefixText} $${this.price(po)} for ${this.timeframe(po, true)}`;
      } else {
        return `${this.trialPrefixText} $${this.price(po)} / ${this.timeframe(po, true)}`;
      }
    },
    injectCookies() {
      this.partnerRef = this.$cookies.get('partner_ref');
      this.sc = this.$cookies.get('sc');
    },
    price(po) {
      return +((po.webAppCost / 100).toFixed(2));
    },
    select(po) {
      this.selected = po;
    },
    timeframe(po, singular = false) {
      let str = null;
      switch(po.monthsUntilExpiration) {
        case 1:
          str = singular ? 'month' : 'monthly';
          break;
        case 12:
          if (po.oneTimeSub) {
            str = '12 months';
          } else {
            str = singular ? 'year' : 'yearly';
          }
          break;
        default:
          str = singular ? `${po.monthsUntilExpiration} months` : `every ${po.monthsUntilExpiration} months`;
      }
      return str;
    },
    toggleOptions() {
      const classification = this.$store.getters['session/classification'];
      const pv = this.$store.getters['productVars/get']['tiered_subscription_classification_id'];
      const tiered = pv && this.user.classificationId == pv.value;
      if (tiered && classification && classification.preferredPurchaseOrderId) {
        this.$store.dispatch('ui/forceBaseUpsellModal', { show: true });
      } else {
        this.showOptions = true;
      }
      this.$analytics.events.viewedMembershipOptions();
    },
    triggerPreferredSubmit() {
      const ref = this.$refs.preferredSubmitForm.$el;
      ref.submit();
    },
    triggerOptionSubmit() {
      const ref = this.$refs.optionSubmitForm.$el;
      ref.submit();
    },
    toggle(params = {}) {
      this.active = true;
      this.showOptions = false;
      this.selected = this.preferredPurchaseOrder;
      this.injectCookies();
      const source = this.$store.getters['ui/upsellModalSource'];
      this.$analytics.events.viewedUpgradeScreen(source);
      this.$analytics.events.viewedPreferredSubscriptionScreen(source.type, source.value);
      this.$store.dispatch('ui/toggleUpsellModal', { show: false, source: {} });
      // start carousel timer
      this.$nextTick(function() {
        const ref = this.$refs.carousel;
        if (ref) ref.setup();
      });
    },
  },
};
</script>

<style lang='scss' scoped>
  .preferred-po-modal-component-container {
    align-items: center;
    background-color: rgba(0,0,0,.7);
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    overflow-x: hidden;
    overflow-y: auto;
    position: fixed;
    right: 0;
    top: 0;
    transition: opacity .3s ease;
    z-index: 1000;

    .content {
      background: white;
      border-radius: 10px;
      box-sizing: border-box;
      display: flex;
      margin: auto 0;
      max-width: rem-calc(1000);
      overflow: hidden;
      position: relative;
      text-align: center;
      transition: transform .3s ease;
      width: 80%;

      @include breakpoint(medium) {
        width: 90%;
      }

      @include breakpoint(small) {
        background: #fafafa;
        border-radius: 0;
        flex-direction: column;
        min-height: 100%;
        width: 100%;
      }

      .close {
        background: hlt-lighten(black, 90%);
        border: 0;
        border-radius: 100%;
        cursor: pointer;
        right: rem-calc(16);
        padding: rem-calc(2);
        position: absolute;
        top: rem-calc(16);

        img {
          height: rem-calc(24);
          width: rem-calc(24);
          display: block;
        }
      }

      .left {
        background: #fafafa;
        flex-basis: 44%;
        padding: rem-calc(64) 0 rem-calc(32) 0;
        width: 44%;

        @include breakpoint(small) {
          flex-basis: 100%;
          padding: rem-calc(32) 0 rem-calc(8) 0;
          width: 100%;
        }

        .upsell-carousel-container.showOptions {
          @include breakpoint(small) {
            display: none;
          }
        }
      }

      .right {
        align-items: center;
        border-left: solid 1px #e8e8e8;
        box-sizing: border-box;
        display: flex;
        flex-basis: 56%;
        flex-direction: column;
        justify-content: center;
        padding: rem-calc(24);
        width: 56%;

        @include breakpoint(small) {
          background: #fafafa;
          flex-basis: 100%;
          margin: auto;
          width: 100%;
        }

        .heading {
          @include charter-font;
          font-size: rem-calc(32);
          margin-bottom: rem-calc(16);

          &:not(.options-heading) {
            @include breakpoint(small) {
              display: none;
            }
          }
        }

        .subheading {
          font-size: rem-calc(16);
          line-height: 1.5;
          letter-spacing: 0.25px;
        }

        .mastery-button {
          background: #8d0098;
          font-weight: $bold;
          margin: rem-calc(32) 0 rem-calc(24) 0;
          width: rem-calc(280);
        }

        .options {
          background: transparent;
          color: #8d0098;
          font-size: rem-calc(16);
          font-weight: $semi-bold;
          letter-spacing: 0.15px;
          text-decoration: underline;
        }

        img.mobile-options-image {
          display: none;

          @include breakpoint(small) {
            display: block;
            padding-bottom: rem-calc(16);
            width: rem-calc(250);
          }
        }

        ul.options-container {
          background: white;
          border-radius: 8px;
          list-style-type: none;
          margin-bottom: rem-calc(8);
          padding: 0;
          max-width: rem-calc(320);
          width: 100%;

          li {
            border-top: 1px solid #E3E1E2;
            cursor: pointer;
            display: flex;
            padding: rem-calc(20) rem-calc(28);

            &:first-child {
              @include breakpoint(small) {
                border: 0;
              }
            }

            &:last-child {
              border-bottom: 1px solid #E3E1E2;

              @include breakpoint(small) {
                border-bottom: 0;
              }
            }

            .bullet {
              border: 1px solid black;
              border-radius: 100%;
              box-sizing: border-box;
              height: rem-calc(18);
              margin: rem-calc(1) rem-calc(20) 0 0;
              padding: rem-calc(2);
              width: rem-calc(18);
            }

            .text {
              text-align: left;

              > div.first {
                color: black;
                font-weight: $semi-bold;
                margin-bottom: rem-calc(4);
              }

              > div.second {
                color: #767676;
                font-size: rem-calc(14);
                letter-spacing: 0.3px;
              }
            }

            &.selected {
              .bullet {
                background: black content-box;
              }
            }
          }
        }
      }
    }
  }
</style>
